import type { Auftrag, PdpPruefung } from '@awf/shared';
import { useAngemeldeterNutzer } from '../auth/auth-context.js';
import { istFremd } from './einsicht.js';
import stile from './kein-bedarf-karte.module.css';
import type { AuftragsQuelle } from './runs-source.js';
import { type StarteVolleAnalyse, VolleAnalyseKnopf } from './volle-analyse-knopf.js';

/**
 * Inhalt der Karte "Kein Optimierungsbedarf" eines PDP-Auftrags (Spec 2026-11-03-kein-bedarf, 4.1
 * und 4.2): was der Vorfilter geprueft hat, und der Weg, die volle Analyse doch noch zu starten.
 * Den Rahmen (`section`) stellt die Ergebnisseite.
 */

const POSITION = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const ANZAHL = new Intl.NumberFormat('de-DE');

/** Nur, was im Lauf ermittelt wurde: Ein fehlendes Feld ergibt keine Zeile (Spec 4.1). */
export function pruefpunkte(pruefung: PdpPruefung): string[] {
  const punkte: string[] = [];
  // Die Position gehoert zur besten Anfrage. `null` heisst "ermittelt, ohne Wert", `undefined`
  // "nicht ermittelt" (keine Zeile). Alles andere ergibt keine Zeile.
  if (pruefung.besteAnfrage && typeof pruefung.position === 'number') {
    const bei =
      typeof pruefung.impressionen === 'number'
        ? ` bei ${ANZAHL.format(pruefung.impressionen)} Impressionen`
        : '';
    punkte.push(
      `Suchwort „${pruefung.besteAnfrage}“: Platz ${POSITION.format(pruefung.position)}${bei} (Ziel: Top 8)`,
    );
  } else if (pruefung.suchwort && pruefung.besteAnfrage === null && pruefung.position === null) {
    punkte.push(`Suchwort „${pruefung.suchwort}“: keine Platzierung gefunden`);
  }
  if (pruefung.h1Passt === true) punkte.push('Überschrift passt zum Produkt');
  if (pruefung.veralteteJahre?.length === 0) punkte.push('Keine veralteten Jahreszahlen im Text');
  if (pruefung.standDatum) punkte.push(`Stand: ${pruefung.standDatum}`);
  return punkte;
}

export function KeinBedarfKarte({
  auftrag,
  quelle,
  starte,
}: {
  auftrag: Auftrag;
  quelle: AuftragsQuelle;
  starte: StarteVolleAnalyse;
}) {
  // Gesamtansicht (`admin`/`technik`): Das Backend startet nur fuer den eigenen Auftrag.
  const fremd = istFremd(auftrag, useAngemeldeterNutzer()?.uid);
  const befund = auftrag.befund;
  const pruefung = befund?.pruefung;
  const punkte = pruefung ? pruefpunkte(pruefung) : [];
  const unsicher = pruefung?.datenlage === 'duenn' || pruefung?.datenlage === 'keine_daten';

  return (
    <>
      <p className={stile.aussage}>Kein Optimierungsbedarf</p>
      {punkte.length > 0 ? (
        <ul className={`eds-text-secondary ${stile.punkte}`}>
          {punkte.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : (
        <p className="eds-text-secondary">{befund?.hinweis}</p>
      )}
      {unsicher && (
        <div className="eds-alert eds-alert--warning" role="status">
          <p>Wenig Suchdaten – die Einschätzung ist unsicher.</p>
        </div>
      )}
      <VolleAnalyseKnopf
        key={auftrag.runId}
        ursprungRunId={auftrag.runId}
        kindRunId={auftrag.volleAnalyseRunId}
        starte={starte}
        quelle={quelle}
        fremd={fremd}
      />
    </>
  );
}
